/**
 * Phase-1 Org-Fundament (· 263) — Standort-Verknüpfung Hauptsitz ↔ Filiale.
 *
 * Platziert in den Stammdaten zwischen „Standort-Code" und „Anschrift als
 * Fahrzeugstandort übernehmen". Ermöglicht:
 *  - Rolle wählen: Hauptsitz oder Filiale (gegenseitig ausschließend)
 *  - registrierten Händler aus Dropdown auswählen → Verknüpfungs-Anfrage senden
 *  - eingehende Anfragen bestätigen/ablehnen (die Gegenseite muss bestätigen)
 *  - Status anzeigen (verknüpfte Filialen / Hauptsitz) + Verknüpfung lösen
 */
import { useEffect, useRef, useState } from "react";
import { Building, GitBranch, Check, X, Send, Unlink, Loader2, Plus, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import BranchSetupWizard from "./BranchSetupWizard";
import useSettingsExpandAll from "../../lib/useSettingsExpandAll";

export default function OrgBranchSettings() {
    const { refresh } = useAuth();
    const [org, setOrg] = useState(null);
    const [mode, setMode] = useState(null); // "hauptsitz" | "filiale"
    const [candidates, setCandidates] = useState([]);
    const [target, setTarget] = useState("");
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const [wizardBranch, setWizardBranch] = useState(null); // Filiale, die eingerichtet wird
    const [showCreate, setShowCreate] = useState(false);
    const [expanded, setExpanded] = useState(false);
    useSettingsExpandAll(setExpanded);
    const didAutoExpand = useRef(false);
    const emptyCreate = { company_name: "", email: "", password: "", first_name: "", last_name: "", street: "", house_number: "", postal_code: "", city: "", country: "Deutschland", site_code: "", phone_office: "" };
    const [createForm, setCreateForm] = useState(emptyCreate);
    const [creating, setCreating] = useState(false);

    const setCreate = (k) => (e) => {
        const v = e.target.value;
        setCreateForm((f) => ({ ...f, [k]: k === "site_code" ? String(v).toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4) : v }));
    };
    const genPassword = () => {
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
        let p = "";
        for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
        setCreateForm((f) => ({ ...f, password: p }));
    };
    const createBranch = async () => {
        if (!createForm.company_name.trim()) { toast.error("Firmierung erforderlich"); return; }
        if (!createForm.email.trim()) { toast.error("E-Mail erforderlich"); return; }
        if ((createForm.password || "").length < 8) { toast.error("Passwort: mindestens 8 Zeichen"); return; }
        if (createForm.site_code && createForm.site_code.length < 2) { toast.error("Standort-Code: 2–4 Großbuchstaben"); return; }
        setCreating(true);
        try {
            const r = await api.post("/org/branches", createForm);
            toast.success(`Filiale „${r.data.branch?.company_name}" angelegt — Login-Daten an die Filiale weitergeben.`);
            setShowCreate(false);
            setCreateForm(emptyCreate);
            await refresh();
            load(); notifyChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anlage fehlgeschlagen");
        } finally { setCreating(false); }
    };

    const load = () => api.get("/org/me").then((r) => setOrg(r.data)).catch(() => {});
    useEffect(() => {
        load();
        const onChanged = () => load();
        window.addEventListener("twowheels:org-changed", onChanged);
        return () => window.removeEventListener("twowheels:org-changed", onChanged);
    }, []);

    // TOC-Sprung „Standorte & Filialen" klappt den Abschnitt auf.
    useEffect(() => {
        const onExpand = () => setExpanded(true);
        window.addEventListener("twowheels:expand-standorte", onExpand);
        return () => window.removeEventListener("twowheels:expand-standorte", onExpand);
    }, []);

    // Auto-aufklappen NUR bei offenen eingehenden Anfragen (handlungsrelevant) —
    // einmalig. Jun-2026 (User-Wunsch): alle Settings-Sektionen starten standardmäßig
    // eingeklappt; reine Hauptsitze werden NICHT mehr automatisch aufgeklappt.
    useEffect(() => {
        if (!org || didAutoExpand.current) return;
        if ((org.pending_incoming || []).length > 0) {
            setExpanded(true);
        }
        didAutoExpand.current = true;
    }, [org]);

    useEffect(() => {
        if (!mode) { setCandidates([]); return undefined; }
        const t = setTimeout(() => {
            api.get("/org/eligible-dealers", { params: { mode, q: search || undefined } })
                .then((r) => setCandidates(r.data)).catch(() => setCandidates([]));
        }, 250);
        return () => clearTimeout(t);
    }, [mode, search]);
    useEffect(() => { setTarget(""); }, [mode]);

    const notifyChanged = () => window.dispatchEvent(new CustomEvent("twowheels:org-changed"));

    const sendRequest = async () => {
        if (!target) { toast.error("Bitte einen Händler auswählen"); return; }
        setBusy(true);
        try {
            await api.post("/org/link-request", { target_dealer_id: target, my_role: mode });
            toast.success("Anfrage gesendet — die Gegenseite muss bestätigen.");
            setMode(null); setTarget("");
            load(); notifyChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage fehlgeschlagen");
        } finally { setBusy(false); }
    };

    const decide = async (rid, action) => {
        setBusy(true);
        try {
            await api.post(`/org/link-request/${rid}/${action}`);
            toast.success(action === "confirm" ? "Verknüpfung bestätigt" : "Anfrage abgelehnt");
            await refresh();
            load(); notifyChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally { setBusy(false); }
    };

    const unlink = async (branchId) => {
        setBusy(true);
        try {
            await api.post(`/org/unlink/${branchId}`);
            toast.success("Verknüpfung gelöst");
            await refresh();
            load(); notifyChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Lösen fehlgeschlagen");
        } finally { setBusy(false); }
    };

    if (!org) return null;
    const role = org.branch_role;
    const summary = role === "hauptsitz"
        ? `Hauptsitz mit ${org.branches.length} Filiale(n)`
        : role === "filiale"
            ? `Filiale von ${org.hauptsitz?.company_name || "—"}`
            : "Noch kein Standort-Verbund — Filialen anlegen oder verknüpfen";

    return (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-testid="org-branch-section">
            <button type="button" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}
                className={`w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 transition-colors ${expanded ? "border-b border-gray-200" : ""}`} data-testid="org-branch-toggle">
                <GitBranch className="w-5 h-5 text-[#2563EB] shrink-0" />
                <div className="min-w-0 flex-1">
                    <h2 className="font-display text-lg font-bold">Standorte &amp; Filialen</h2>
                    <p className="text-xs text-gray-600 truncate mt-1">{summary}</p>
                </div>
                {(org.pending_incoming || []).length > 0 && (
                    <span className="shrink-0 text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5" data-testid="org-pending-badge">
                        {org.pending_incoming.length} Anfrage(n)
                    </span>
                )}
                <span className={`text-gray-400 text-lg transition-transform shrink-0 ${expanded ? "rotate-180" : ""}`} aria-hidden="true">⌄</span>
            </button>

            {expanded && (
            <div className="px-6 pb-6" data-testid="org-branch-settings">
            <div className="flex items-center gap-2 mb-2">
                <p className="text-xs text-gray-500 flex-1">
                    Mehrere Standorte unter einer Firmierung führen. Filialen übernehmen die zentralen Einstellungen des Hauptsitzes. Neue Filialen können hier direkt mit eigenem Login angelegt werden.
                </p>
                {role !== "filiale" && (
                    <Button onClick={() => { setCreateForm(emptyCreate); setShowCreate(true); }}
                        className="ml-auto h-7 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[11px] rounded-sm shrink-0" data-testid="org-create-branch-btn">
                        <Plus className="w-3 h-3 mr-1" /> Neue Filiale anlegen
                    </Button>
                )}
            </div>

            {/* Eingehende Anfragen (müssen bestätigt werden) */}
            {(org.pending_incoming || []).length > 0 && (
                <div className="mb-3 space-y-2" data-testid="org-incoming-requests">
                    {org.pending_incoming.map((r) => (
                        <div key={r.id} className="flex items-center justify-between gap-2 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2">
                            <div className="text-xs">
                                <span className="font-bold">Verknüpfungs-Anfrage:</span>{" "}
                                {(r.hauptsitz_label?.company_name) || "—"} (Hauptsitz) ↔ {(r.filiale_label?.company_name) || "—"} (Filiale)
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <Button onClick={() => decide(r.id, "confirm")} disabled={busy} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] rounded-sm" data-testid={`org-confirm-${r.id}`}><Check className="w-3 h-3 mr-1" />Bestätigen</Button>
                                <Button onClick={() => decide(r.id, "reject")} disabled={busy} variant="outline" className="h-7 text-[11px] rounded-sm border-red-300 text-red-600" data-testid={`org-reject-${r.id}`}><X className="w-3 h-3 mr-1" />Ablehnen</Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Aktueller Status */}
            {role === "filiale" && org.hauptsitz && (
                <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="org-status-filiale">
                    <div className="text-xs"><Building className="w-3 h-3 inline mr-1" /> Filiale von <span className="font-bold">{org.hauptsitz.company_name}</span>{org.hauptsitz.site_code ? ` (${org.hauptsitz.site_code})` : ""}</div>
                    <Button onClick={() => unlink(org.self.id)} disabled={busy} variant="outline" className="h-7 text-[11px] rounded-sm border-red-300 text-red-600" data-testid="org-leave-btn"><Unlink className="w-3 h-3 mr-1" />Verknüpfung lösen</Button>
                </div>
            )}
            {role === "hauptsitz" && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="org-status-hauptsitz">
                    <div className="text-xs font-bold mb-1"><Building className="w-3 h-3 inline mr-1" /> Hauptsitz mit {org.branches.length} Filiale(n)</div>
                    {org.branches.map((b) => {
                        const addr = [b.street, [b.postal_code, b.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
                        return (
                            <div key={b.id} className="flex items-start justify-between gap-2 py-1.5 border-t border-blue-100 first:border-0">
                                <button type="button" onClick={() => setWizardBranch(b)} title="Filiale einrichten"
                                    className="min-w-0 flex-1 text-left group cursor-pointer" data-testid={`org-branch-open-${b.id}`}>
                                    <div className="text-xs font-medium truncate inline-flex items-center gap-1 group-hover:text-[#2563EB] transition-colors">
                                        {b.company_name}{b.site_code ? ` · ${b.site_code}` : ""}
                                        <Settings2 className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                    </div>
                                    {addr && <div className="text-[11px] text-gray-500 truncate" data-testid={`org-branch-address-${b.id}`}>{addr}</div>}
                                </button>
                                <button onClick={() => unlink(b.id)} disabled={busy} className="text-[11px] text-red-600 hover:underline shrink-0 mt-0.5" data-testid={`org-unlink-${b.id}`}>entkoppeln</button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Neue Verknüpfung anstoßen — nur wenn standalone (oder Hauptsitz erweitert) */}
            {role !== "filiale" && (
                <div className="mt-3">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Rolle wählen</Label>
                    <div className="flex gap-4 mt-1.5">
                        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                            <input type="radio" name="org-mode" checked={mode === "hauptsitz"} onChange={() => setMode("hauptsitz")} data-testid="org-mode-hauptsitz" />
                            Hauptsitz (Filiale hinzufügen)
                        </label>
                        <label className={`flex items-center gap-1.5 text-sm cursor-pointer ${role === "hauptsitz" ? "opacity-40 pointer-events-none" : ""}`}>
                            <input type="radio" name="org-mode" checked={mode === "filiale"} onChange={() => setMode("filiale")} disabled={role === "hauptsitz"} data-testid="org-mode-filiale" />
                            Filiale (Hauptsitz auswählen)
                        </label>
                    </div>
                    {mode && (
                        <div className="mt-2 space-y-2">
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Suchen (Firma, Standort-Code, Ort)…"
                                className="w-full border border-gray-300 rounded-sm h-9 px-2 text-sm bg-white"
                                data-testid="org-search"
                            />
                            <div className="flex flex-col sm:flex-row gap-2">
                                <select value={target} onChange={(e) => setTarget(e.target.value)} className="flex-1 border border-gray-300 rounded-sm h-10 px-2 text-sm bg-white" data-testid="org-target-select">
                                    <option value="">— {mode === "hauptsitz" ? "Filiale" : "Hauptsitz"} auswählen ({candidates.length}) —</option>
                                    {candidates.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {`${c.company_name}${c.site_code ? ` (${c.site_code})` : ""}${c.city ? ` · ${c.city}` : ""}`}
                                        </option>
                                    ))}
                                </select>
                                <Button onClick={sendRequest} disabled={busy || !target} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm" data-testid="org-send-request-btn">
                                    <Send className="w-4 h-4 mr-1" /> Anfrage senden
                                </Button>
                            </div>
                        </div>
                    )}
                    {(org.pending_outgoing || []).length > 0 && (
                        <div className="mt-2 text-[11px] text-gray-500" data-testid="org-outgoing">
                            {org.pending_outgoing.length} gesendete Anfrage(n) — warten auf Bestätigung der Gegenseite.
                        </div>
                    )}
                </div>
            )}
            </div>
            )}

            <BranchSetupWizard
                branchId={wizardBranch?.id}
                open={!!wizardBranch}
                onClose={() => setWizardBranch(null)}
                onSaved={() => { load(); notifyChanged(); }}
            />

            <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
                <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="branch-create-dialog">
                    <DialogHeader>
                        <DialogTitle>Neue Filiale anlegen</DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-gray-500 -mt-2">
                        Es wird ein eigener Händler-Login (Filiale) angelegt. Die Login-Daten gibst du selbst vor und teilst sie der Filiale mit. Aktive Hauptsitz-Einstellungen werden sofort übernommen.
                    </p>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Firmierung *</Label>
                            <Input value={createForm.company_name} onChange={setCreate("company_name")} className="rounded-sm mt-1" data-testid="create-company-name" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Login-E-Mail *</Label>
                                <Input type="email" value={createForm.email} onChange={setCreate("email")} className="rounded-sm mt-1" data-testid="create-email" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Passwort * (min. 8)</Label>
                                <div className="flex gap-1 mt-1">
                                    <Input value={createForm.password} onChange={setCreate("password")} className="rounded-sm font-mono-num" data-testid="create-password" />
                                    <Button type="button" variant="outline" onClick={genPassword} className="rounded-sm shrink-0 text-[11px]" data-testid="create-gen-password">Generieren</Button>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Vorname (Ansprechpartner)</Label>
                                <Input value={createForm.first_name} onChange={setCreate("first_name")} className="rounded-sm mt-1" data-testid="create-first-name" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nachname</Label>
                                <Input value={createForm.last_name} onChange={setCreate("last_name")} className="rounded-sm mt-1" data-testid="create-last-name" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div className="sm:col-span-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Straße</Label>
                                <Input value={createForm.street} onChange={setCreate("street")} className="rounded-sm mt-1" data-testid="create-street" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Hausnr.</Label>
                                <Input value={createForm.house_number} onChange={setCreate("house_number")} className="rounded-sm mt-1" data-testid="create-house-number" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">PLZ</Label>
                                <Input value={createForm.postal_code} onChange={setCreate("postal_code")} className="rounded-sm mt-1 font-mono-num" data-testid="create-postal" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Ort</Label>
                                <Input value={createForm.city} onChange={setCreate("city")} className="rounded-sm mt-1" data-testid="create-city" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Standort-Code (2–4 Großbuchst.)</Label>
                                <Input value={createForm.site_code} onChange={setCreate("site_code")} placeholder="FBWE" className="rounded-sm mt-1 font-mono-num uppercase max-w-[140px]" data-testid="create-site-code" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Telefon (Geschäft)</Label>
                                <Input value={createForm.phone_office} onChange={setCreate("phone_office")} className="rounded-sm mt-1 font-mono-num" data-testid="create-phone" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating} className="rounded-sm" data-testid="create-branch-cancel">Abbrechen</Button>
                        <Button onClick={createBranch} disabled={creating} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-sm" data-testid="create-branch-submit">
                            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Filiale anlegen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
